import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/Modal/Modal';
import { Button } from '@/components/Button/Button';
import { Alert } from '@/components/Alert/Alert';
import { t } from '@/lib/i18n';
import { useSaveJobAsTemplate } from '../hooks';
import styles from './SaveAsTemplateModal.module.css';

export interface SaveAsTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  initialTitle: string;
  instructions: string;
  onSaved: (templateId: string) => void;
}

const FORM_ID = 'save-template-modal-form';

export function SaveAsTemplateModal({
  open,
  onOpenChange,
  jobId,
  initialTitle,
  instructions,
  onSaved,
}: SaveAsTemplateModalProps) {
  const [name, setName] = useState(initialTitle);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const save = useSaveJobAsTemplate(jobId);

  // Reset state and focus the name input when the modal opens
  useEffect(() => {
    if (open) {
      setName(initialTitle);
      setError(null);
      // Manual focus instead of autoFocus to keep keyboard users in control
      const id = requestAnimationFrame(() => inputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [open, initialTitle]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim() || save.isPending) return;

    save.mutate(
      { title: name.trim() },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          onSaved(data.item.id);
        },
        onError: () => {
          setError(t('Could not save the template. Try again.'));
        },
      },
    );
  }

  const isSubmitDisabled = !name.trim() || save.isPending;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('Save as Template')}
      size="sm"
      actions={
        <>
          <Button variant="secondary" type="button" onClick={() => onOpenChange(false)}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={FORM_ID}
            disabled={isSubmitDisabled}
            isLoading={save.isPending}
          >
            {t('Save →')}
          </Button>
        </>
      }
    >
      {error ? <Alert variant="error">{error}</Alert> : null}
      <form id={FORM_ID} className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.field}>
          <label htmlFor="template-name" className={styles['field-label']}>
            {t('Name')}
          </label>
          <input
            ref={inputRef}
            id="template-name"
            className={styles.input}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            required
          />
        </div>
        <div className={styles.field}>
          <span className={styles['field-label']}>{t('Instructions')}</span>
          <div className={styles.preview}>{instructions}</div>
        </div>
        <p className={styles.hint}>{t('Edit templates in the Library.')}</p>
      </form>
    </Modal>
  );
}
